import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { router } from '@inertiajs/react';
import { cn } from '@/lib/utils';
import { Loader2, History } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import type { WorkflowDetail, WorkflowRun } from '../types';

interface SectionProps {
  workflow: WorkflowDetail;
}

export const ExecutionsSection = ({ workflow }: SectionProps) => {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const getStatusString = (status: number) => {
    switch (status) {
      case 1:
        return 'running';
      case 2:
        return 'completed';
      case 3:
        return 'failed';
      case 4:
        return 'canceled';
      default:
        return 'unknown';
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 2: // completed
        return 'text-green-600 bg-green-50';
      case 3: // failed
        return 'text-red-600 bg-red-50';
      case 1: // running
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  useEffect(() => {
    const fetchRuns = async () => {
      try {
        console.log('Fetching runs for workflow:', workflow.function_name);
        const response = await fetch(
          `/workflows?name=${encodeURIComponent(workflow.function_name)}&format=json`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch workflow runs');
        }
        const data = await response.json();
        console.log('Raw API response:', data);

        // Extract workflows directly from the response
        const workflows = data.workflows || [];
        console.log('Extracted workflows:', workflows);

        if (Array.isArray(workflows)) {
          // Transform the data to match our WorkflowRun interface
          const transformedRuns = workflows.map((workflow: any, index: number) => ({
            id: workflow.id || `temp-${index}`,
            workflowId: workflow.id || '',
            status: workflow.status || 0,
            startTime: workflow.startTime || '',
            endTime: workflow.endTime || '',
            executionTime: workflow.executionTime || '',
            runId: workflow.runId || `temp-run-${index}`,
            name: workflow.name || '',
          }));
          console.log('Setting runs with array of length:', transformedRuns.length);
          setRuns(transformedRuns);
        } else {
          console.log('No valid workflows array found');
          setRuns([]);
        }
      } catch (error) {
        console.error('Error fetching runs:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to fetch workflow runs',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    void fetchRuns();
  }, [workflow.function_name, toast]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  return (
    <div className="animate-in fade-in-50 duration-200">
      <section>
        <h2 className="text-xl font-semibold mb-6">Execution History</h2>
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : runs.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Run ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run, index) => {
                  console.log('Rendering run:', run);
                  const uniqueKey = run.runId || `temp-run-${index}`;
                  return (
                    <TableRow
                      key={uniqueKey}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => router.visit(`/workflows/${run.id}/runs/${run.runId}`)}
                    >
                      <TableCell>
                        <div
                          className={cn(
                            'inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-sm font-medium',
                            getStatusColor(run.status)
                          )}
                        >
                          {run.status === 1 && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          {getStatusString(run.status)}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(run.startTime)}</TableCell>
                      <TableCell>{formatDate(run.executionTime)}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {run.runId ? `${run.runId.slice(0, 8)}...` : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-6 md:p-8 rounded-lg border bg-muted/30 text-center">
            <History className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Executions</h3>
            <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base">
              This workflow hasn&apos;t been executed yet. Use the Run tab to start a new execution.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};
